import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MessageService, MenuItem } from 'primeng/api';
import { DeliveryOrderService, AppDataService } from 'app/services';
import { Utils } from 'app/utils';
import { DriverDetailComponent } from 'app/components/home/drivers/driver-detail/driver-detail.component';
import {
    ChatMessageTypeEnum,
    OrderRiderActionEnum,
    PaymentCardTypeEnum,
    BetaFeature,
    BookingDetailsTransaction,
    XpressUserRoleEnum
} from 'app/models';
import * as moment from 'moment/moment';
import { StylesStatus } from 'app/models/users/StylesStatus';

enum MenuTab {
    OrderDetail = 1,
    Customer = 2,
    Timeline = 3,
    Messages = 4,
    Payment = 5
}

enum TransactionStatus {
    Paid = 'PAID',
    Failed = 'FAILED'
}

@Component({
    selector: 'driver-order-detail',
    templateUrl: './driver-order-detail.component.html',
    styleUrls: ['./driver-order-detail.component.scss',
        '../../../../../../styles/sidebar.scss']
})
export class DriverOrderDetailComponent implements OnInit {
    public orderId: number = 0;
    @Input()
    set _orderId(value: number) {
        if (value > 0) {
            this.orderId = value;
            this.getRiderOrderDetail();
        }
    }

    public riderId: number = 0;
    @Input()
    set _riderId(value: number) {
        if (value > 0) {
            this.riderId = value;
        }
    }

    @Input()
    public driverDetailComponent!: DriverDetailComponent;

    public isVisibleDisplayOrderDetail: boolean = false;
    @Input()
    set _isVisibleDisplayOrderDetail(value: boolean) {
        this.isVisibleDisplayOrderDetail = value;
    }

    @Output() changedVisibleDisplayOrderDetailEmit = new EventEmitter<boolean>();
    changedVisibleDisplayOrderDetail(increased: any) {
        this.changedVisibleDisplayOrderDetailEmit.emit(increased);
    }

    constructor(private messageService: MessageService,
        private deliveryOrderService: DeliveryOrderService, public appDataService: AppDataService) { }

    public chatMessageType = ChatMessageTypeEnum;
    public paymentCardType = PaymentCardTypeEnum;

    public menuTab = MenuTab;
    public activeTab: MenuTab = MenuTab.OrderDetail;
    public menuItems: MenuItem[] = [
        {
            label: 'Details',
            command: (e: any) => this.menuTabChange(MenuTab.OrderDetail)
        },
        {
            label: 'Customer',
            command: (e: any) => this.menuTabChange(MenuTab.Customer)
        },
        {
            label: 'Payment',
            command: (e: any) => this.menuTabChange(MenuTab.Payment)
        },
        {
            label: 'Messages',
            command: (e: any) => this.menuTabChange(MenuTab.Messages)
        },
        {
            label: 'Timeline',
            command: (e: any) => this.menuTabChange(MenuTab.Timeline)
        }
    ];

    public riderOrderDetail: any;
    public isOrderDetailSpinner: boolean = false;
    public isPhoneNumberVerified: boolean = true;

    public bookingTransactionDetails!: BookingDetailsTransaction;
    public orderRiderActionEnum = OrderRiderActionEnum;

    public customerId!: number;
    public isVisibleCustomerInfo: boolean = false;

    public betaFeature = BetaFeature;

    ngOnInit(): void { }

    // Operator accounts are identified by role only. Do not use OperatorId — admins can carry a
    // linked OperatorId from GetAuthData while viewing drivers in the admin portal.
    public get isOperatorAccount(): boolean {
        return +this.appDataService.authData.roleId === XpressUserRoleEnum.Operator;
    }

    public getRiderOrderDetail() {
        this.isOrderDetailSpinner = true;

        const dto = {
            OrderId: this.orderId,
            TimeZoneOffset: Utils.getTimezoneOffset()
        };
        this.deliveryOrderService.getRiderDeliveryOrderDetail(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.isOrderDetailSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.riderOrderDetail = data;
                this.riderOrderDetail.Amount = `₱ ${this.riderOrderDetail.Amount.toFixed(2) }`;
				this.riderOrderDetail.ServiceFeeAmount = `₱ ${this.riderOrderDetail.ServiceFeeAmount.toFixed(2) }`;
                this.riderOrderDetail.InitialTotalFareAmount = `₱ ${this.riderOrderDetail.InitialTotalFareAmount.toFixed(2) }`;
                this.riderOrderDetail.TipAmount = `₱ ${this.riderOrderDetail.TipAmount.toFixed(2) }`;
                this.riderOrderDetail.BaseFare = `₱ ${this.riderOrderDetail.BaseFare.toFixed(2) }`;
                this.riderOrderDetail.DistanceFare = `₱ ${this.riderOrderDetail.DistanceFare.toFixed(2) }`;
                this.riderOrderDetail.TripDurationCharge = `₱ ${this.riderOrderDetail.TripDurationCharge.toFixed(2) }`;
                this.riderOrderDetail.SurgeCharges = `₱ ${this.riderOrderDetail.SurgeCharges.toFixed(2) }`;
                this.riderOrderDetail.CreatedAt = moment(this.riderOrderDetail.CreatedAt).format('D MM YYYY');
                this.riderOrderDetail.OrderRequestedDate = moment(this.riderOrderDetail.OrderRequested).format('D MM YYYY');
                this.riderOrderDetail.OrderAcceptedDate = (this.riderOrderDetail.OrderAccepted) ?
                    moment(this.riderOrderDetail.OrderAccepted).format('D MM YYYY') : '';
                this.riderOrderDetail.ArrivedToPickupTime = (this.riderOrderDetail.ArrivedToPickupTime)
                    ? moment(this.riderOrderDetail.ArrivedToPickupTime).format('hh:mm') : '';
                this.riderOrderDetail.PickupTime = (this.riderOrderDetail.PickupTime)
                    ? moment(this.riderOrderDetail.PickupTime).format('hh:mm') : '';
                this.riderOrderDetail.DroppedOffTime = (this.riderOrderDetail.DroppedOffTime) ?
                    moment(this.riderOrderDetail.DroppedOffTime).format('hh:mm') : '';
                this.riderOrderDetail.CompletedTime = (this.riderOrderDetail.CompletedTime) ?
                    moment(this.riderOrderDetail.CompletedTime).format('hh:mm') : '';
                this.riderOrderDetail.UserCreatedAt = moment(this.riderOrderDetail.UserCreatedAt).format('D MMM YYYY');
                this.isOrderDetailSpinner = false;
                this.riderOrderDetail.OrderAttempts.forEach((attempt: any) => {
                    attempt.CreatedAt = moment(attempt.CreatedAt).format('hh:mm:ss');
                });
                this.riderOrderDetail.PickupSurgeAmountText = (this.riderOrderDetail.PickupSurgeAmount > 0) ?
                    `₱${this.riderOrderDetail.PickupSurgeAmount}` : '-';
                this.riderOrderDetail.WaitingFeeAmountText = (this.riderOrderDetail.WaitingFeeAmount > 0) ?
                    `₱${this.riderOrderDetail.WaitingFeeAmount}` : '-';
                this.riderOrderDetail.OrderAcceptedTime = (this.riderOrderDetail.OrderAccepted) ? moment(this.riderOrderDetail.OrderAccepted).format('hh:mm') : '';
                this.riderOrderDetail.OrderChats.forEach((message: any) => {
                    message.CreatedAt = Utils.utcToLocaleString(message.CreatedAt);
                });
                this.riderOrderDetail.DropoffOrderTimes.forEach((item: any) => {
                    item.CreatedAtTime = (item.CreatedAt) ? moment(item.CreatedAt).format('hh:mm') : '';
                });
                this.riderOrderDetail.UserRating = this.riderOrderDetail.UserRating.toFixed(1);
                this.riderOrderDetail.TotalSpend = `₱ ${this.riderOrderDetail.TotalSpend.toFixed(2)}`;
                this.riderOrderDetail.PreviousWalletBalance = `₱ ${(this.riderOrderDetail.PreviousWalletBalance ?? 0).toFixed(2)}`;
                this.riderOrderDetail.CurrentWalletBalance = `₱ ${(this.riderOrderDetail.CurrentWalletBalance ?? 0).toFixed(2)}`;
                this.riderOrderDetail.PickupFareAmountText = `₱ ${(this.riderOrderDetail.PickupFareAmount ?? 0).toFixed(2)}`;

                this.bookingTransactionDetails = new BookingDetailsTransaction(data.XpressPaymentTransaction);
            })
            .catch(err => {
                this.isOrderDetailSpinner = false;
                throw err;
            });
    }

    getPriority() {
        return this.riderOrderDetail.IsPriority ? 'VIP' : 'Regular';
    }

    public getStatusStyles(status: any): StylesStatus {
        let backgroundColor: string;
        let textColor: string;
        switch (status) {
            case TransactionStatus.Paid:
                backgroundColor = '#1CBA70';
                textColor = '#FFF';
                break;
            case TransactionStatus.Failed:
                backgroundColor = '#EB4236';
                textColor = '#FFF';
                break;
            default:
                backgroundColor = '#FFC043';
                textColor = '#664D1B';
                break;
        }

        return {
            backgroundColor,
            color: textColor
        };
    }

    public menuTabChange(menuTab: MenuTab) {
        this.activeTab = menuTab;

        if (this.activeTab === MenuTab.OrderDetail) {
            this.getRiderOrderDetail();
        }
    }

    getPhoneVerificationStatus(): string {
        return this.isPhoneNumberVerified
            ? 'Verified' : 'Not Verified';
    }

    public handleCustomerInfoClick(customerId: number): void {
        this.customerId = customerId;
        this.isVisibleCustomerInfo = true;
    }
    changedVisibleCustomerInfoEmit(value: any) {
        this.isVisibleCustomerInfo = value;
    }

    cancel() {
        this.changedVisibleDisplayOrderDetail(false);
    }
}
